import { trackEvent } from 'dibs-tracking';
import { VisitCountModel } from 'dibs-visit-tracking/exports/visitCountModel';
import { KEY_REG_PROMPT_COUNT } from 'dibs-visit-tracking/exports/visitCountConstants';

import { FlowActions } from '../constants';

const LOGIN = 'LOGIN' as const;
const REGISTER = 'REGISTER' as const;
const AUTO_PROMPT_REGISTER = 'AUTO_PROMPT_REGISTER' as const;
const TRADE_APPLICATION = 'TRADE_APPLICATION' as const;
const SUSPECTED_TRADE_APPLICATION = 'SUSPECTED_TRADE_APPLICATION' as const;

export type PostAuthTrackingFlowActions = Extract<
    FlowActions,
    | typeof LOGIN
    | typeof REGISTER
    | typeof AUTO_PROMPT_REGISTER
    | typeof TRADE_APPLICATION
    | typeof SUSPECTED_TRADE_APPLICATION
>;

const actions = {
    loginFormInteraction: 'login form interaction',
    registrationFormInteraction: 'registration form interaction',
    tradeApplicationInteraction: 'trade application form interaction',
    personalizationFormInteraction: 'personalization form interaction',
    userSurveyConfirmationInteraction: 'personalization form confirmation',
    userTypeSurveyDisplayed: 'user type survey displayed',
    userTypeSurveyCompleted: 'user type survey completed',
} as const;

const categories = {
    formError: 'form error',
    login: 'login',
    registration: 'registration',
    tradeApplication: 'trade application',
} as const;

const triggerTypes = {
    [LOGIN]: 'login link',
    [REGISTER]: 'register link',
    [AUTO_PROMPT_REGISTER]: 'pageview limit reached',
    [TRADE_APPLICATION]: 'trade application',
    [SUSPECTED_TRADE_APPLICATION]: 'suspected trade application',
} as const;

const modeOfEntryLabel = (function () {
    const modeOfEntryMap = {
        [AUTO_PROMPT_REGISTER]: 'sitewide registration modal',
        [LOGIN]: 'sitewide login modal',
        [REGISTER]: 'sitewide registration modal',
        [TRADE_APPLICATION]: 'trade application',
        [SUSPECTED_TRADE_APPLICATION]: 'suspected nav',
    } as const;

    return (flowAction: PostAuthTrackingFlowActions) =>
        flowAction ? modeOfEntryMap[flowAction] : flowAction;
})();

function getRegPromptCount(): number {
    const visitCountModel = new VisitCountModel();
    return visitCountModel.get(KEY_REG_PROMPT_COUNT);
}

export function trackConsumerAdditionalInfoProvided(flowAction: PostAuthTrackingFlowActions): void {
    const [action, category, flowActionLabel] =
        flowAction === LOGIN
            ? [actions.loginFormInteraction, categories.login, 'login']
            : [actions.registrationFormInteraction, categories.registration, 'registration'];

    trackEvent({
        action,
        category,
        label: `${flowActionLabel} step completed (zip code)`,
        additional: { triggerType: triggerTypes[flowAction] },
    });
}

export function trackDealerApplicationLinkClicked(flowAction: PostAuthTrackingFlowActions): void {
    const [action, category] =
        flowAction === LOGIN
            ? [actions.loginFormInteraction, categories.login]
            : [actions.registrationFormInteraction, categories.registration];

    trackEvent({
        action,
        category,
        label: 'link to dealer application clicked',
        additional: { triggerType: triggerTypes[flowAction] },
    });
}

export function trackBackButtonClick(): void {
    trackEvent({
        category: categories.registration,
        action: 'back button clicked',
        label: 'pageview limit reached',
    });
}

export function trackTradeApplicationStep1Completed(
    flowAction: PostAuthTrackingFlowActions,
    altVerification = false
): void {
    const verificationDetail = altVerification
        ? 'alternative verification selected'
        : 'verified by website';
    const label = `${modeOfEntryLabel(
        flowAction
    )}|trade step completed (trade application) - ${verificationDetail}|`;

    trackEvent({
        action: actions.tradeApplicationInteraction,
        category: categories.tradeApplication,
        label,
        value: flowAction === AUTO_PROMPT_REGISTER ? 1 : 0,
        additional: { triggerType: triggerTypes[flowAction] },
    });
}

export function trackTradeFormEntry(flowAction: PostAuthTrackingFlowActions): void {
    trackEvent({
        action: 'trade application form entry',
        category: categories.tradeApplication,
        label: modeOfEntryLabel(flowAction),
        value: flowAction === AUTO_PROMPT_REGISTER ? 1 : 0,
        additional: { triggerType: triggerTypes[flowAction] },
    });
}

export function trackTradeFormError({
    field = '',
    description = '',
}: {
    field: string;
    description: string;
}): void {
    trackEvent({
        action: `trade|trade application|${field}`,
        category: categories.formError,
        label: description,
    });
}

export function trackTradeFormSubmitted(flowAction: PostAuthTrackingFlowActions): void {
    trackEvent({
        action: 'trade application complete',
        category: categories.tradeApplication,
        label: modeOfEntryLabel(flowAction),
        value: flowAction === AUTO_PROMPT_REGISTER ? 1 : 0,
        additional: { triggerType: triggerTypes[flowAction] },
    });
}

type ProvidedVerfication = {
    websiteProvided?: boolean;
    docUploaded?: boolean;
    docToBeProvided?: boolean;
    socialVerification?: boolean;
};

export function trackTradeProfileCompleted(
    flowAction: PostAuthTrackingFlowActions,
    { websiteProvided, docUploaded, docToBeProvided, socialVerification }: ProvidedVerfication
): void {
    let verificationDetail = '';
    if (websiteProvided) {
        verificationDetail = 'verified by website';
    } else if (docUploaded) {
        verificationDetail = 'verified by docs uploaded';
    } else if (docToBeProvided) {
        verificationDetail = 'verified by docs to be provided';
    } else if (socialVerification) {
        verificationDetail = 'verified by social media';
    }

    trackEvent({
        action: actions.tradeApplicationInteraction,
        category: categories.tradeApplication,
        label: `${modeOfEntryLabel(
            flowAction
        )}|trade step completed (trade firm profile) - ${verificationDetail}`,
        value: flowAction === AUTO_PROMPT_REGISTER ? 1 : 0,
        additional: { triggerType: triggerTypes[flowAction] },
    });
}

export function trackTradeVerificationFormCompleted(
    flowAction: PostAuthTrackingFlowActions,
    {
        docUploaded,
        docToBeProvided,
        socialVerification,
    }: Exclude<ProvidedVerfication, 'websiteProvided'>
): void {
    let verificationDetail = '';
    if (docUploaded) {
        verificationDetail = 'verified by docs uploaded';
    } else if (docToBeProvided) {
        verificationDetail = 'verified by docs to be provided';
    } else if (socialVerification) {
        verificationDetail = 'verified by social media';
    }

    trackEvent({
        action: actions.tradeApplicationInteraction,
        category: categories.tradeApplication,
        label: `${modeOfEntryLabel(
            flowAction
        )}|trade step completed (trade alternative verification) - ${verificationDetail}`,
        value: flowAction === AUTO_PROMPT_REGISTER ? 1 : 0,
        additional: { triggerType: triggerTypes[flowAction] },
    });
}

export function trackUserSurveyConfirmation(): void {
    trackEvent({
        action: actions.userSurveyConfirmationInteraction,
        category: categories.registration,
        label: `my account clicked`,
        additional: { triggerType: triggerTypes.REGISTER },
    });
}

export function trackBackButton(): void {
    trackEvent({
        action: actions.personalizationFormInteraction,
        category: categories.registration,
        label: 'back button selected',
    });
}

export function trackPersonalization({
    flowAction,
    stepLabel,
    personalizationLabel,
    surveyResponse,
}: {
    flowAction: PostAuthTrackingFlowActions | null;
    stepLabel: string;
    personalizationLabel: string;
    surveyResponse?: string | null;
}): void {
    let additional;
    if (flowAction) {
        additional = {
            triggerType: triggerTypes[flowAction],
        };
    }
    if (surveyResponse) {
        additional = {
            ...additional,
            surveyResponse,
        };
    }

    trackEvent({
        action: actions.personalizationFormInteraction,
        category: flowAction === LOGIN ? categories.login : categories.registration,
        label: `personalization step ${stepLabel} (${personalizationLabel})`,
        additional,
    });
}

export function trackUserTypeSelectionEntry(flowAction: PostAuthTrackingFlowActions): void {
    trackEvent({
        category: flowAction === LOGIN ? categories.login : categories.registration,
        action: actions.userTypeSurveyDisplayed,
        label: 'none',
        additional: { triggerType: triggerTypes[flowAction] },
        value: flowAction === AUTO_PROMPT_REGISTER ? getRegPromptCount() : 0,
    });
}

export function trackUserTypeSelectionComplete(
    flowAction: PostAuthTrackingFlowActions,
    { userType = '' }: { userType: string }
): void {
    trackEvent({
        category: flowAction === LOGIN ? categories.login : categories.registration,
        action: actions.userTypeSurveyCompleted,
        label: userType,
        additional: { triggerType: triggerTypes[flowAction] },
        value: flowAction === AUTO_PROMPT_REGISTER ? getRegPromptCount() : 0,
    });
}
